import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { userState } from 'state';
import { EnumUserType, type User, useSearchCustomersQuery } from 'lib/graphql';
import { isProcessor as isProcessorHelper } from 'lib/userHelpers';

export interface UseCustomerOptionsConfig {
  includeAddNew?: boolean;
  relationshipType?: EnumUserType;
  producerId?: string;
}

export function useCustomerOptions(
  vendorId: string | undefined,
  isOpen: boolean,
  options?: UseCustomerOptionsConfig,
) {
  const { relationshipType, producerId } = options ?? {};
  const currentUser = useRecoilValue(userState);
  const userIsProcessor = currentUser
    ? isProcessorHelper(currentUser as User)
    : false;
  const shouldLoad = Boolean(isOpen && vendorId);

  // Only include producerId filter when NOT a processor. Processors should see all their customers.
  const effectiveProducerId = userIsProcessor ? undefined : producerId;

  const variables = {
    vendorId: vendorId as string,
    ...(relationshipType ? { relationshipType } : {}),
    ...(effectiveProducerId ? { producerId: effectiveProducerId } : {}),
  } as {
    vendorId: string;
    relationshipType?: EnumUserType;
    producerId?: string;
  };

  const { data, refetch } = useSearchCustomersQuery({
    variables,
    skip: !shouldLoad,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (isOpen && vendorId) {
      refetch?.(variables);
    }
  }, [
    isOpen,
    vendorId,
    effectiveProducerId,
    relationshipType,
    refetch,
    userIsProcessor,
  ]);

  const customerOptions = (data?.searchCustomers ?? []).map((customer) => {
    const name =
      `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim();
    const label = customer.company
      ? `${customer.company} (${name || customer.email || customer._id})`
      : name || customer.email || customer._id;
    return {
      _id: customer._id,
      fullName: label,
      company: customer.company,
      email: customer.email,
      phone: customer.phone,
      first_name: customer.first_name,
      last_name: customer.last_name,
    };
  });

  return { customerOptions, refetchCustomers: refetch };
}
